// Performance V2 — Keywords tab (#4295). Account-wide keyword performance
// with Quality Score, IS metrics, and CSV export.
// Extracted from performance-v2.tsx (#4932 Phase 0.3); no behavior change.
// The keywords query lives HERE (it was gated on `tab === 'keywords'` in the
// shell; the module only mounts when the tab is active, so mounting preserves
// the lazy fetch and its Google Ads quota rationale). Column visibility stays
// shell-owned: the header toolbar's ColumnsPopover and the userSettings
// persistence live there.
import { Spinner } from '@/components/glitch-ui/spinner';
import type React from 'react';
import { BarChart3 } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';

import type { KeywordOverviewData } from '@/lib/apis';
import { useKeywordsOverviewReportQuery } from '@/lib/query-options';
import { DataTable } from '@/components/glitch-ui/data-table';
import { QualityScorePill } from '@/components/glitch-ui/quality-score-pill';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import type { ExportColumn } from '@/lib/csv-export';
import {
  fmt, MatchTypeBadge, KeywordStatusBadge, ImpressionShareCell, PerformanceCell,
  EmptyTabState, ExportCsvButton,
} from '../cells';

// Column-picker metadata for the Keywords table. 'performance' is the
// consolidated Imp · Clicks · Conv · Cost cell — on by default; the granular
// columns it consolidates default off so the table renders compact out of
// the box. Exported for the shell's header toolbar (ColumnsPopover) and the
// userSettings column-visibility restore.
export const KEYWORDS_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean; defaultHidden?: boolean }[] = [
  { id: 'keyword',       label: 'Keyword',         pinned: true },
  { id: 'quality_score', label: 'QS' },
  { id: 'performance',   label: 'Performance' },
  { id: 'impressions',   label: 'Impressions',     defaultHidden: true },
  { id: 'clicks',        label: 'Clicks',          defaultHidden: true },
  { id: 'conversions',   label: 'Conversions',     defaultHidden: true },
  { id: 'cost',          label: 'Cost',            defaultHidden: true },
  { id: 'ctr',           label: 'CTR' },
  { id: 'avg_cpc',       label: 'Avg. CPC' },
  { id: 'conv_rate',     label: 'Conv. rate' },
  { id: 'cost_per_conv', label: 'Cost/conv' },
  { id: 'searchIS',      label: 'Search IS' },
  { id: 'exactIS',       label: 'Exact-match IS' },
];
export const KEYWORDS_COLUMN_IDS = new Set(KEYWORDS_TABLE_COLUMNS.map(c => c.id));
export const KEYWORDS_PINNED_COLUMN_IDS = new Set(KEYWORDS_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));
export const KEYWORDS_DEFAULT_VISIBLE_IDS = new Set(KEYWORDS_TABLE_COLUMNS.filter(c => !c.defaultHidden).map(c => c.id));

export function KeywordsTab({
  campaignIds,
  visibleColumns,
  currencySymbol,
  dateRangeLabel,
  dateRangeFilter,
  layout,
  onReorder,
  onResize,
  renderChart,
}: {
  campaignIds: string | undefined;
  visibleColumns: Set<string>;
  currencySymbol: string;
  dateRangeLabel: string;
  dateRangeFilter: { start_date: string; end_date: string };
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
  renderChart: () => React.ReactNode;
}) {
  const cs = currencySymbol;

  // #4295 — account-wide keyword performance (with Quality Score). Only
  // fetched when the user is actually viewing the Keywords tab (this module
  // mounts on tab activation); that's a sub-1k row table per account and the
  // GAds quota cost is real.
  const { data: keywordsData, isLoading: isKeywordsLoading } = useKeywordsOverviewReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: !!campaignIds },
  );

  // #4295 — Keywords table columns. One row per (ad_group_id, criterion_id).
  // The "Keyword" cell carries match-type + status indicators so the table
  // doesn't need separate columns for each (saves horizontal real estate).
  // QualityScorePill renders the point-in-time QS merged backend-side.
  const keywordColumns: ColumnDef<KeywordOverviewData, unknown>[] = [
    { accessorKey: 'keyword', header: 'Keyword', cell: ({ row }) => (
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{row.original.keyword}</span>
          <MatchTypeBadge matchType={row.original.match_type} />
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <KeywordStatusBadge status={row.original.status} />
          <span className="text-xs text-gray-500">{row.original.campaign_name}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500 truncate max-w-[180px]">{row.original.ad_group_name}</span>
        </div>
      </div>
    )},
    { accessorKey: 'quality_score', header: 'QS', cell: ({ row }) => <QualityScorePill score={row.original.quality_score} /> },
    // Consolidated Imp · Clicks · Conv · Cost cell, plus the four granular
    // metric columns it consolidates. The granular ones default-off via the
    // column picker (KEYWORDS_TABLE_COLUMNS.defaultHidden) — users opt in
    // when they want to sort by a single metric.
    { id: 'performance', header: 'Performance', enableSorting: false, cell: ({ row }) => (
      <PerformanceCell
        impressions={row.original.impressions}
        clicks={row.original.clicks}
        conversions={row.original.conversions}
        cost={row.original.cost}
        currency={cs}
      />
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
    { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
    { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
    { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
    { id: 'searchIS', header: 'Search IS', cell: ({ row }) => <ImpressionShareCell value={row.original.search_impression_share} /> },
    { id: 'exactIS', header: 'Exact-match IS', cell: ({ row }) => {
      const v = row.original.search_exact_match_impression_share;
      if (v == null) return <span className="text-xs text-gray-400">—</span>;
      return <span className="text-sm">{(v * 100).toFixed(1)}%</span>;
    }},
  ];
  // Filter keyword columns by user-chosen visibility — column picker writes
  // through to user settings; defaults exclude the granular metrics in
  // favour of Performance + derived columns.
  const visibleKeywordColumnDefs = keywordColumns.filter(c => {
    const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
    return id ? visibleColumns.has(id) : true;
  });

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          case 'performance-chart':
            return renderChart();

          case 'keywords-content': {
            const hasKeywords = Array.isArray(keywordsData) && keywordsData.length > 0;
            if (isKeywordsLoading && !hasKeywords) {
              return (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" className="text-gray-400" />
                  <span className="ml-3 text-sm text-gray-500">Loading keyword performance…</span>
                </div>
              );
            }
            if (!keywordsData || keywordsData.length === 0) {
              return (
                <EmptyTabState
                  icon={BarChart3}
                  title="No Keyword Data"
                  description="Keyword performance will appear here once campaigns have traffic in the selected period."
                />
              );
            }
            // #4302 — CSV export for Keywords. Matches the Google Ads UI
            // Search keyword report column set (including QS + IS metrics).
            const keywordExportColumns: ExportColumn<KeywordOverviewData>[] = [
              { header: 'Keyword', value: r => r.keyword },
              { header: 'Match type', value: r => r.match_type ?? '' },
              { header: 'Status', value: r => r.status ?? '' },
              { header: 'Campaign', value: r => r.campaign_name },
              { header: 'Ad group', value: r => r.ad_group_name },
              { header: 'Quality Score', value: r => r.quality_score ?? '' },
              { header: 'Impressions', value: r => r.impressions },
              { header: 'Clicks', value: r => r.clicks },
              { header: 'CTR', value: r => `${(r.ctr * 100).toFixed(2)}%` },
              { header: 'Avg. CPC', value: r => r.avg_cpc },
              { header: 'Cost', value: r => r.cost },
              { header: 'Conversions', value: r => r.conversions },
              { header: 'Conv. rate', value: r => `${(r.conv_rate * 100).toFixed(2)}%` },
              { header: 'Cost/conv', value: r => r.cost_per_conv },
              { header: 'Search IS', value: r => r.search_impression_share != null ? `${(r.search_impression_share * 100).toFixed(2)}%` : '' },
              { header: 'Exact-match IS', value: r => r.search_exact_match_impression_share != null ? `${(r.search_exact_match_impression_share * 100).toFixed(2)}%` : '' },
            ];
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-end pt-1 pr-10">
                  <ExportCsvButton
                    rows={keywordsData}
                    columns={keywordExportColumns}
                    title="Search keyword report"
                    dateRange={dateRangeLabel}
                    startDate={dateRangeFilter.start_date}
                    endDate={dateRangeFilter.end_date}
                    filenameSlug="search-keyword-report"
                  />
                </div>
                <DataTable<KeywordOverviewData>
                  data={keywordsData}
                  columns={visibleKeywordColumnDefs}
                  pageSize={25}
                  variant="bordered"
                  emptyState="No keyword performance data available for this period"
                />
                {/* #6281 — the report is capped server-side; a full page silently
                    read as "everything" when it was a truncation. */}
                {keywordsData.length >= 1000 && (
                  <p className="text-xs text-gray-500">
                    Showing the first 1,000 keywords by impressions. Narrow the date range or campaign filter to see the rest.
                  </p>
                )}
              </div>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
